// SPDX-License-Identifier: AGPL-3.0-or-later

import type {ComponentType, SVGProps} from 'react';

export interface HolidaySidePeeker {
	id: string;
	Svg: ComponentType<SVGProps<SVGSVGElement>>;
	/** Positive shifts the sprite into the right wall, as a % of its own width. */
	offsetXPercent?: number;
	/** Raise above the member list at rest so wrapping art can overlap it. */
	overlayMemberList?: boolean;
}

export interface HolidaySidePeekPack {
	id: string;
	/** Seasonal packs beat the year-round baseline when both are enabled. */
	seasonal: boolean;
	peekers: ReadonlyArray<HolidaySidePeeker>;
	chance: () => number;
	isEnabled: () => boolean;
	/** Preferred on-screen height. Width follows each peeker's aspect. */
	heightRem: number;
	/** Cap vs the chat column so oversized art still fits. */
	maxWidthPercent: number;
	maxHeightPercent: number;
}

const packs: Array<HolidaySidePeekPack> = [];

export function registerHolidaySidePeekPack(pack: HolidaySidePeekPack): () => void {
	if (!packs.includes(pack)) {
		packs.push(pack);
	}
	return () => {
		const index = packs.indexOf(pack);
		if (index >= 0) packs.splice(index, 1);
	};
}

function packIsEnabled(pack: HolidaySidePeekPack): boolean {
	try {
		return pack.isEnabled();
	} catch {
		return false;
	}
}

export function resolveHolidaySidePeekPack(): HolidaySidePeekPack | null {
	const enabled = packs.filter(packIsEnabled);
	return enabled.find((pack) => pack.seasonal) ?? enabled.find((pack) => !pack.seasonal) ?? null;
}

export function getHolidaySidePeeker(packId: string, peekerId: string): HolidaySidePeeker | null {
	const pack = packs.find((item) => item.id === packId);
	return pack?.peekers.find((peeker) => peeker.id === peekerId) ?? null;
}

export function getHolidaySidePeekPack(packId: string): HolidaySidePeekPack | null {
	return packs.find((pack) => pack.id === packId) ?? null;
}
